import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../../styles/SubscriptionContainer.module.scss';
import SubscriptionAppBar from '../../../components/SubscriptionAppBar';
import Button from '../../../../../components/Button';
import { useHistory, useLocation } from 'react-router-dom';
import { usePurchaseSubscriptionContext } from '../../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import { inAppCancellation as events } from '../../../../../lib/analytics/events';
import retentionOfferDogImage from '../../../../../assets/images/in_app_cancellation/retention_offer_dog.jpeg';
import { BaseInAppCancellationState } from '../../util/types';
import AppPaths from '../../../../../AppPaths';
import pauseImage from '../../../../../assets/images/pause.png';
import { ConfirmModal } from '../../../../../components/modals';
import { DateTime } from 'luxon';
import { useMutation } from '@apollo/client';
import { pauseMembershipMutation } from '../../../../../graphql-operations';
import { gqlTypes } from '../../../../../types';
import ErrorScreen from '../../../components/ErrorScreen/ErrorScreen';
import Loading from '../../../../../components/Loading';
import ErrorMessage from '../../../../../components/ErrorMessage';

export default function PauseMembership() {
  const { t } = useTranslation();
  const NUM_TERMS_TO_PAUSE = 1; // As of 10/25, we only allow users to pause for one month at a time.
  const { device } = usePurchaseSubscriptionContext();
  const history = useHistory();
  const location = useLocation<BaseInAppCancellationState>();
  const [error, setError] = useState('');
  const { subscriptionCategory } = location.state;
  if (!device.pet) {
    throw new Error('No pet found');
  }

  events.pageLoad({ petId: device.pet.id, subscriptionCategory }, 'Pause Membership');

  const petName = device.pet.name;
  const petAvatarPath = device.pet.photos.first?.image?.fullSize ?? retentionOfferDogImage;

  const [pauseMutation, pauseMutationState] = useMutation<
    gqlTypes.ECOMMERCE_pauseMembership,
    gqlTypes.ECOMMERCE_pauseMembershipVariables
  >(pauseMembershipMutation, {
    onCompleted: (data) => {
      const success = !!data.pauseMembership?.success;
      if (!success) {
        setError(
          data.pauseMembership?.reasonForFailure
            ? t('error_reason', { reason: data.pauseMembership.reasonForFailure })
            : t('failed_to_pause_membership_contact_customer_support'),
        );
      } else {
        history.push({
          pathname: AppPaths.Subscription.PauseMembershipConfirmed(device.moduleId),
          search: window.location.search,
          state: { subscriptionCategory },
        });
      }
    },
  });

  if (pauseMutationState.loading) {
    return <Loading />;
  }

  if (!device.subscription?.id) {
    return <ErrorScreen />;
  }

  const handleClaimPauseMembership = () => {
    events.claimPauseMembership({ petId: device.pet!.id, subscriptionCategory });
    pauseMutation({
      variables: {
        input: {
          recurlySubscriptionId: device.subscription!.id,
          moduleId: device.moduleId,
          numTerms: NUM_TERMS_TO_PAUSE,
        },
      },
    });
  };

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <SubscriptionAppBar
          backButtonAnalyticsEvent={() =>
            events.claimPauseMembershipBack({ petId: device.pet?.id, subscriptionCategory })
          }
          closeButtonAnalyticsEvent={() =>
            events.claimPauseMembershipClose({ petId: device.pet?.id, subscriptionCategory })
          }
        />
        <div className={styles.subscriptionContainer}>
          <div className={styles.content}>
            <div className={styles.largeHeader}>
              <h1>{t('consider_a_pause')}</h1>
            </div>

            <p>
              {t(
                'were_happy_to_pause_pet_name_membership_for_a_month_if_you_need_we_wont_bill_you_while_paused_no_questions_asked',
                {
                  petName,
                },
              )}
            </p>
            {error && <ErrorMessage errors={[error]} />}

            <div className={styles.offerContainer}>
              <div className={styles.offer}>
                <div className={styles.description}>{t('pause_membership_for')}</div>
                <div className={styles.pauseDuration}>{t('1_month_lowercase')}</div>
              </div>
            </div>

            <div className={styles.pauseMonthOffer}>
              <div className={styles.pauseMonthOfferImages}>
                <div className={styles.pauseMonthOfferAvatar}>
                  <img src={petAvatarPath} alt={petName} />
                </div>
                <div className={styles.pauseMonthOfferPauseIcon}>
                  <img src={pauseImage} alt={t('pause_button')} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <ConfirmModal
            trigger={<Button className={styles.button}>{t('pause_membership')}</Button>}
            body={
              <>
                <h2>{t('are_you_sure')}</h2>
                <p>
                  {t(
                    'this_will_pause_your_access_to_the_fi_app_gps_tracking_and_pet_name_data_on_date_and_skip_billing_for_1_month',
                    {
                      petName,
                      date: DateTime.fromISO(device.subscription?.currentTermEndsAt).toFormat('L/d/yy'),
                    },
                  )}
                </p>
              </>
            }
            acceptText={t('confirm')}
            declineText={t('no_go_back')}
            onAccept={handleClaimPauseMembership}
          />
          <Button
            className={styles.button}
            tertiary={true}
            onClick={() => {
              history.push({
                pathname: AppPaths.Subscription.Cancel(device.moduleId),
                state: { subscriptionCategory: subscriptionCategory },
                search: window.location.search,
              });
            }}
          >
            {t('continue_with_cancellation')}
          </Button>
        </div>
      </div>
    </>
  );
}
